import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import classes from './StatTile.module.css';

interface StatTileProps {
  readonly to: string;
  readonly icon: ReactNode;
  readonly label: string;
  /** Число (счётчик). undefined — плитка без числа (напр. «Чаты»). */
  readonly value?: number;
  /** Единица (склонённая через i18next-плюрал), напр. «прайсов». */
  readonly unit?: string;
  /** Бейдж «N новых» (показываем при count > 0). */
  readonly badge?: string;
  /** Приглушённая подпись вместо числа (напр. «Скоро»). */
  readonly hint?: string;
  /** Приглушённая строка ПОД числом (напр. заявленный тоннаж рядом с числом заявок). */
  readonly subtext?: string;
  /** Призыв при нулевом счётчике вместо «0 …» (напр. «Создать прайс»). */
  readonly emptyCta?: string;
  readonly loading?: boolean;
}

// Плитка-ссылка раздела с живым счётчиком. Тонкая: данные считает родитель (AppHome).
export default function StatTile({
  to,
  icon,
  label,
  value,
  unit,
  badge,
  hint,
  subtext,
  emptyCta,
  loading,
}: StatTileProps) {
  return (
    <Card component={Link} to={to} withBorder padding="lg" className={classes.tile} style={{ textDecoration: 'none' }}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          {icon}
          {badge ? (
            <Badge color="brand" variant="light" size="sm">
              {badge}
            </Badge>
          ) : null}
        </Group>

        <Text fw={600} c="var(--mantine-color-text)">
          {label}
        </Text>

        {loading ? (
          <Skeleton h={28} w={80} />
        ) : value != null ? (
          value === 0 && emptyCta ? (
            <Text size="sm" c="brand.7" fw={500}>
              {emptyCta}
            </Text>
          ) : (
            <Group gap={6} align="baseline" wrap="nowrap">
              <Text fz={28} fw={700} lh={1}>
                {value}
              </Text>
              {unit ? (
                <Text size="sm" c="gray.6">
                  {unit}
                </Text>
              ) : null}
            </Group>
          )
        ) : hint ? (
          <Text size="sm" c="gray.6">
            {hint}
          </Text>
        ) : null}

        {/* Подпись под числом (в отличие от hint, который его заменяет). */}
        {!loading && subtext ? (
          <Text size="sm" c="gray.6">
            {subtext}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
